import { Utils } from '../../utils/Utils';
import { ITournament, Tournament } from '@/store/defaultObjects';
import { ActionContext } from 'vuex';
import store from '@/store';
import { ACTION } from '@/store/ACTION';

type State = {
  tournament: {
    data: ITournament;
    players: any[];
    conditionsMinRounds: {
      minRounds: number;
      minRoundsProgress: number;
    }
  };
  isShowingCommonPopup: boolean;
};

export type PlayersTournament = {
  players: {
    score: number;
    place: number;
    prize: number;
    player: string;
  }[];
};

// @ts-ignore
export default {
  state: {
    tournament: {
      data: Tournament,
      players: [],
      conditionsMinRounds: {
        minRounds: null,
        minRoundsProgress: null,
      }
    },
    isShowingCommonPopup: false
  },
  getters: {
    getPlayersTournament(state: State) {
      if (!state?.tournament?.players) return {};
      return state.tournament.players;
    },
    getRules(state: State) {
      if (!state?.tournament?.data?.rules) return {};

      return state.tournament.data.rules;
    },
    hasTournament(state: State) {
      return Boolean(state.tournament.data && state.tournament.data.name && state.tournament.data.stage);
    },
    getAutoCharge(state: State, getters: any) {
      if (!getters.hasTournament) return false;

      return state.tournament.data.rules.autoCharging;
    },
    getTournamentStartData(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return Utils.formatDate(new Date(state.tournament.data.startedAt));
    },
    getTournamentFinishData(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return Utils.formatDate(new Date(state.tournament.data.finishedAt));
    },
    getTournamentFinishedAt(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return state.tournament.data.finishedAt;
    },
    getTournamentClosedAt(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return state.tournament.data.closedAt;
    },
    getTournamentGameNamesList(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return state.tournament.data.rules.games.map(data => data.name).join(', ');
    },
    getTournamentMinBet(state: State, getters: any) {
      if (!getters.hasTournament) return 0;

      const cents = state.tournament.data.rules.minBet;
      const currencyDecimals = state.tournament.data.rules.currencyDecimals;
      const currencyCode = state.tournament.data.rules.currencyCode;

      return Utils.centsToMoneyStr(cents, currencyDecimals, currencyCode);
    },

    getTournamentMinRounds(state: State, getters: any) {
      if (!getters.hasTournament) return 0;
      return state.tournament.data.rules.minRounds;
    },
    getTournamentTotalMoney(state: State, getters: any) {
      if (!getters.hasTournament) return 0;

      const sum = state.tournament.data.rules.prizes.reduce(
        (prev, curr) => prev + curr.amount * (Math.abs(curr.from - curr.to) + 1),
        0
      );
      const currencyDecimals = state.tournament.data.rules.currencyDecimals;
      const currencyCode = state.tournament.data.rules.currencyCode;

      return Utils.centsToMoneyStr(sum, currencyDecimals, currencyCode);
    },
    getTournamentTotalFreeSpins(state: State, getters: any) {
      if (!getters.hasTournament) return 0;

      const totalFreeSpins = state.tournament.data.rules.prizes.reduce(
        (prev, curr) => prev + (curr.amountFS ? curr.amountFS : 0) * (Math.abs(curr.from - curr.to) + 1),
        0
      );
      const gameName = state.tournament.data.rules.prizes.find(item => item.amountFSGameName);
      const i18n = promoApp.i18n;
      const localization = i18n.messages[i18n.locale] || i18n.messages['en'];
      const inGame = localization?.common_popup?.in || '';
      return {
        totalFreeSpins,
        gameName: gameName?.amountFSGameName ? `${inGame} ${gameName?.amountFSGameName || ''}` : ''
      };
    },
    getTournamentTotalPrize(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      const rewardMoney =
        Number(getters.getTournamentTotalMoney) !== 0
          ? getters.getTournamentTotalMoney + ' ' + Utils.formatCurrencyCodeToMili(getters.getTournamentCurrency)
          : '';

      const { totalFreeSpins, gameName } = getters.getTournamentTotalFreeSpins;
      const rewardFreeSpins = totalFreeSpins ? `${totalFreeSpins} FS ${gameName}` : '';
      const plus = rewardMoney && rewardFreeSpins ? '+' : '';

      return `${rewardMoney} ${plus} ${rewardFreeSpins}`;
    },
    getTournamentCurrency(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return getters.getRules.currencyCode;
    },
    getTournamentShowingWelcomePopup(state: State, getters: any) {
      return getters.getIsShowingWelcomePopup && getters.hasTournament;
    },
    getTournamentShowingIcon(state: State, getters: any) {
      return getters.getIsShowingIcon && getters.hasTournament;
    },
    getTournamentClosedTime(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return state.tournament.data.closedAt;
    },
    getTournamentFinishedTime(state: State, getters: any) {
      if (!getters.hasTournament) return '';

      return state.tournament.data.finishedAt;
    },
    getShowingCommonPopup(state: State, getters: any) {
      return state.isShowingCommonPopup && getters.hasTournament;
    },
    getMinRounds(state: State, getters: any) {
      if (!getters.hasTournament) return '';
      return state.tournament?.conditionsMinRounds?.minRounds;
    },
    getMinRoundsProgress(state: State, getters: any) {
      if (!getters.hasTournament) return '';
      return state.tournament?.conditionsMinRounds?.minRoundsProgress;
    }
    
  },
  mutations: {
    showCommonPopup(state: State) {
      state.isShowingCommonPopup = true;
    },
    hideCommonPopup(state: State) {
      state.isShowingCommonPopup = false;
    },
    setTournamentData(state: State, data: ITournament) {
      state.tournament.data = data;

      const rules = state.tournament.data.rules;
      const { winToBetPoints, betPoints, winPoints, roundPoints } = rules;

      if (winToBetPoints) {
        rules.winToBetPoints = Number(winToBetPoints);
        // rules.winToBetPoints = Number(winToBetPoints).toFixed(2);
      }

      if (betPoints) {
        rules.betPoints = Number(betPoints);
        // rules.betPoints = Number(betPoints).toFixed(2);
      }

      if (roundPoints) {
        rules.roundPoints = Number(roundPoints);
        // rules.roundPoints = Number(roundPoints).toFixed(2);
      }

      if (winPoints) {
        // rules.winPoints = Number(winPoints).toFixed(2);
        rules.winPoints = Number(winPoints);
      }

      promoApp.eventEmitter.emit(ACTION.SETTINGS_CORRECT_PROMO);
    },
    setTournamentPlayersData(state: State, { players }: PlayersTournament) {
      state.tournament.players = players;
    },
    setMinRounds(state: State, value: number) {
      state.tournament.conditionsMinRounds.minRounds = value;
    },
    setMinRoundsProgress(state: State, value: number) {
      state.tournament.conditionsMinRounds.minRoundsProgress = value;
    }
  },
  actions: {
    SHOW_COMMON_POPUP({ commit, state }: ActionContext<State, any>) {
      commit('showCommonPopup');
      promoApp.eventEmitter.emit(ACTION.REQUIRE_TOURNAMENT_PLAYERS, { id: state.tournament.data.id });
    },
    HIDE_COMMON_POPUP({ commit }: ActionContext<any, any>) {
      commit('hideCommonPopup');
    },
    SET_TOURNAMENT_DATA({ commit }: ActionContext<any, any>, data: ITournament) {
      if (!data) return;
      commit('setTournamentData', data);
      commit('updateActivityList');
    },
    SET_TOURNAMENT_PLAYERS_DATA({ commit }: ActionContext<any, any>, data: { players: PlayersTournament, conditions: { minRounds: number }, progress: { rounds: number }}) {
      if (!data) return;
      if(data?.conditions?.minRounds){
        commit('setMinRounds', data.conditions.minRounds);
      }
      if(data?.progress?.rounds){
        commit('setMinRoundsProgress', data.progress.rounds);
      }
      
      commit('setTournamentPlayersData', data);
      commit('updateActivityList');
    },
    CLEAR_TOURNAMENT({ commit }: ActionContext<any, any>) {
      if (!store.getters.hasTournament) return;

      commit('setTournamentData', Tournament);
      commit('settingsCorrectPromo');
    }
  }
};
